import {Component, Plugins} from '@packages/systems/wfdl';
import {EElement, ERecord, EText, Expression} from '@packages/systems/wfdl-ast';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {generateGuid} from '@packages/utilities/guid';

/**
 * Splits text by line breaks and creates an array of String and LineBreak elements.
 * Line breaks can be \n, \r, or \r\n.
 *
 * @param text - The text to split
 * @returns An array of Expression elements containing String and LineBreak elements
 *
 * @example
 * // Input: "Hello\nWorld"
 * // Output: [
 * //   EElement({ type: 'Basic::String', data: EText('Hello') }),
 * //   EElement({ type: 'Basic::LineBreak', data: ERecord({}) }),
 * //   EElement({ type: 'Basic::String', data: EText('World') })
 * // ]
 */
export function splitTextIntoElementsWithLineBreaks(
  text: string
): Expression[] {
  // Split by line breaks (\r\n, \r, or \n)
  const textSegments = text.split(/\r\n|\r|\n/);

  // If there's only one segment, no line breaks were found
  if (textSegments.length === 1) {
    return [createStringElement(text)];
  }

  // Create elements for each segment and line break
  const elements: Expression[] = [];
  const lastIndex = textSegments.length - 1;
  textSegments.forEach((segment, index) => {
    // Add text element if segment is not empty
    if (segment) {
      elements.push(createStringElement(segment));
    }

    // Add line break after each segment except the last one
    if (index < lastIndex) {
      elements.push(createLineBreakElement());
    }
  });

  return elements;
}

function createStringElement(text: string): Expression {
  return EElement({
    id: generateGuid(),
    type: qualify(Plugins.name('Basic'), Component.name('String')),
    data: EText(text),
  });
}

function createLineBreakElement(): Expression {
  return EElement({
    id: generateGuid(),
    type: qualify(Plugins.name('Basic'), Component.name('LineBreak')),
    data: ERecord({}),
  });
}
